/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { PersonStanding, Sparkles } from 'lucide-react';
import { cloneElement, FC, ReactElement, ReactNode } from 'react';

import { useUserContext } from '../Root';

import { HighlightVariables } from './HighlightVariables';
import { LiveAgentStatusCheck, LiveAgentStatusHelp, LiveAgentStatusWarning } from './LiveAgentStatusIcons';
import { LastReadEmail } from './LiveAgentSteps/LastReadEmail';
import { LastReadSMS } from './LiveAgentSteps/LastReadSMS';
import { Step } from './LiveAgentTypes';

import { ReadEmailAction, ReadSMSAction } from '@/commons/data';
import Spinner from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

type Operator = 'equals' | 'notEquals' | 'contains' | 'notContains' | 'matches' | 'notMatches';

const getOperatorLabel = (operator: Operator) => {
    switch (operator) {
        case 'equals':
            return 'equals';
        case 'notEquals':
            return 'does not equal';
        case 'contains':
            return 'contains';
        case 'notContains':
            return 'does not contain';
        case 'matches':
            return 'matches';
        case 'notMatches':
            return 'does not match';
        default:
            return operator;
    }
};

export const LiveAgentStepHeader: FC<{
    step: Step;
    icon: ReactElement;
    title: ReactNode;
    subtitle?: ReactNode;
    isCollapsed?: boolean;
    variables: { [key: string]: string };
    precomputedVariables?: { [key: string]: string };
    updateOpenStep?: (stepId: string, force?: boolean) => void;
    hidePlaywright?: boolean;
}> = ({
    step,
    icon,
    title,
    subtitle,
    isCollapsed,
    variables,
    precomputedVariables = {},
    updateOpenStep,
    hidePlaywright = false,
}) => {
    const { isAdmin, isImpersonated } = useUserContext().user;
    const hasAdminPermissions = isAdmin ?? isImpersonated;
    const collapsed = step.executed ?? isCollapsed;

    if (collapsed) {
        let text = '';
        switch (step.kind) {
            case 'separator':
            case 'empty':
                // nothing to render
                break;
            case 'aiPrompt':
                text = `Intent prompt: ${step.intentPrompt ?? step.intent}`;
                break;
            case 'aiAtomic':
                switch (step.action) {
                    case 'leftClick':
                        text = `Click ${step.intent ?? step.target}`;
                        break;
                    case 'typeText':
                        text = `Enter "${step.value}" in ${step.intent ?? step.target}`;
                        break;
                    case 'pressEnter':
                        text = `Press enter on ${step.intent ?? step.target}`;
                        break;
                    case 'selectOption':
                        text = `Pick "${step.value}" in ${step.intent ?? step.target}`;
                        break;
                    case 'hover':
                        text = `Hover over ${step.intent ?? step.target}`;
                        break;
                    case 'extractSelector':
                        text = `Find selector for ${step.target} and store it in {{${step.variableName}}}`;
                }
                break;
            case 'gotoUrl':
                text = `Go to ${step.url}`;
                break;
            case 'wait':
                text = `Wait for ${Number(step.durationMs) / 1000} ${Number(step.durationMs) > 1000 ? 'seconds' : 'second'}`;
                break;
            case 'pressKey':
                text = `Press key: ${step.key}`;
                break;
            case 'emailSignup':
                text =
                    step.intent !== 'confirmationCode-save'
                        ? `Sign up with ${step.signupMethod}`
                        : `Store confirmation code in {{${step.saveCodeToVariable}}}`;
                break;
            case 'emailLogin':
                text = `Log in with ${step.address}`;
                break;
            case 'readSMS':
                text = `Read SMS from ${step.phoneNumber}, to ${step.toVariableName}`;
                break;
            case 'simpleAssertion':
                text = `Assert that ${step.input} ${getOperatorLabel(step.operator as Operator)} ${step.value}`;
                break;
            case 'aiAssertion':
                text = `Assert that ${step.assertion}`;
                break;
            case 'execPlaywright':
                text = `${(step.pwCode ?? '').split('\n')[0]}`;
                break;
            case 'aiExtractInfo':
                text = `Extract information: ${step.description} and store in {{${step.variableName}}}`;
                break;
            case 'recordedActions':
                text = `Recorded actions`;
                break;
            case 'block':
                text = `Block: ${step.name}`;
                break;
            default:
                // exhaustive check, so TS type checker warns us if we forget to support a step kind
                step.kind satisfies never;
        }

        // Report information from the step's execution that could be useful to troubleshoot the story
        let stepFeedback: string | undefined = '';
        if (step.actions && step.actions.length > 0) {
            switch (step.kind) {
                case 'aiAssertion':
                    stepFeedback = step.actions[0].observations;
                    break;
                case 'simpleAssertion':
                    stepFeedback = `The assertion ${step.actions.every((action) => action.result === 'PASS') ? 'passed' : 'failed'}`;
                    break;
                default:
                    if (
                        step.actions.some((action) => action.error) ||
                        step.result === 'CRASH' ||
                        step.result === 'FAIL'
                    ) {
                        stepFeedback = 'The step has crashed';
                        if (step.result === 'FAIL') {
                            stepFeedback = 'The step has failed';
                        }
                        const errorAction = step.actions?.find((action) => action.error);
                        if (errorAction) {
                            stepFeedback = JSON.stringify(errorAction?.error as string);
                        }
                    }
                    break;
            }
        }

        const feedbackColor = step.result === 'PASS' ? 'text-slate-900' : 'text-red-600';
        const fileUploadURL = step?.mediaAssets?.fileUpload?.resolvedURL;
        const fileUploadLabel = step.result === 'PASS' ? 'Uploaded file' : 'File intended for upload';

        const hasReadEmailAction = step.actions?.find((a) => a.kind === 'readEmail') as ReadEmailAction | undefined;
        const hasReadSMSAction = step.actions?.find((a) => a.kind === 'readSMS') as ReadSMSAction | undefined;

        const playwrightCurrentSelector =
            (hasAdminPermissions &&
                !hidePlaywright &&
                step.kind === 'aiAtomic' &&
                step.actions?.[0]?.selectors?.playwrightCurrent?.code) ||
            '';

        return (
            <div className="gap-x-2 flex text-slate-500 items-center truncate text-subtle group/stepheader">
                {step.running && <Spinner />}
                {!(step.kind === 'recordedActions' && !step.targetingStrategy) &&
                    step.kind !== 'block' &&
                    step.kind !== 'empty' &&
                    step.executed &&
                    step.result &&
                    !step.running && (
                        <>
                            {step.result === 'PASS' && <LiveAgentStatusCheck />}
                            {(step.result === 'UNKNOWN' || step.result === 'CANCELLED') && <LiveAgentStatusHelp />}
                            {step.result !== 'PASS' && step.result !== 'UNKNOWN' && step.result !== 'CANCELLED' && (
                                <LiveAgentStatusWarning />
                            )}
                        </>
                    )}
                {cloneElement(icon, { className: 'text-purple-400 shrink-0 self-start' })}
                {step.targetingStrategy && step.kind === 'aiAtomic' && (
                    <>
                        {step.targetingStrategy === 'ai' ? (
                            <Sparkles size="16px" className="shrink-0 self-start mt-1" />
                        ) : (
                            <PersonStanding size="16px" className="shrink-0 self-start mt-1" />
                        )}
                    </>
                )}
                <span className="space-y-0.5 min-w-0">
                    <button
                        onClick={() => {
                            updateOpenStep?.(step.id);
                        }}
                        className="truncate autoExpand group-hover/stepheader:whitespace-normal group-hover/stepheader:transition-[white-space] [transition-behavior:allow-discrete] delay-200 text-left w-full"
                    >
                        <HighlightVariables
                            text={text}
                            variables={variables}
                            precomputedVariables={precomputedVariables}
                        />
                    </button>
                    {playwrightCurrentSelector && (
                        <div
                            title={playwrightCurrentSelector}
                            className="truncate autoExpand group-hover/stepheader:whitespace-normal group-hover/stepheader:transition-[white-space] [transition-behavior:allow-discrete] delay-200 font-mono text-slate-500 font-[300] text-detail text-[10px] [text-box:trim-both] leading-tight"
                        >
                            {playwrightCurrentSelector}
                        </div>
                    )}
                    {fileUploadURL && (
                        <div title={fileUploadLabel + ': ' + fileUploadURL} className={'truncate'}>
                            {fileUploadLabel}: {fileUploadURL}
                        </div>
                    )}
                    {stepFeedback && (
                        <div
                            title={stepFeedback}
                            className={
                                'truncate autoExpand group-hover/stepheader:whitespace-normal group-hover/stepheader:transition-[white-space] [transition-behavior:allow-discrete] delay-200' +
                                ' ' +
                                feedbackColor +
                                ' ' +
                                (['execPlaywright'].includes(step.kind) ? 'font-mono' : '')
                            }
                        >
                            {stepFeedback}
                        </div>
                    )}
                    {hasReadEmailAction && <LastReadEmail readEmailAction={hasReadEmailAction} />}
                    {hasReadSMSAction && <LastReadSMS readSMSAction={hasReadSMSAction} />}
                </span>
            </div>
        );
    }

    return (
        <div className="gap-x-2 flex text-slate-900 items-center truncate text-subtle w-full">
            {cloneElement(icon, { className: 'text-purple-400 shrink-0' })}
            <button
                onClick={() => {
                    updateOpenStep?.(step.id);
                }}
                className={cn(
                    'text-subtle-medium text-left',
                    step.kind !== 'separator' && step.kind !== 'block' && 'w-full',
                )}
            >
                {title}
            </button>
            {subtitle && <span className="text-slate-500 truncate py-1">{subtitle}</span>}
        </div>
    );
};
